<template>
  <div>
    <pa-admin-toolbar
      :per-page="perPage"
      :search.sync="searchInput"
      :from.sync="fromInput"
      :to.sync="toInput"
      :busy="loading"
      @per-page="setPerPage"
      @search="applySearch"
    >
      <button type="button" class="paa-btn paa-btn--secondary" :disabled="loading || !total" @click="exportRows">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" /></svg>
        Export All
      </button>
    </pa-admin-toolbar>

    <div class="paa-filters">
      <pa-admin-filter label="SEO Manager" :value="filters.campaignManager" :options="managerOptions" @input="setFilter('campaignManager', $event)" />
    </div>

    <div v-if="loadError" class="paa-error">{{ loadError }}</div>

    <div class="paa-stat-row">
      <span class="paa-muted">{{ stats.ordersCount || 0 }} booster orders in range</span>
      <span class="paa-spacer"></span>
      <b>Total paid: {{ money(totalPaid) || '$0.00' }}</b>
    </div>

    <div class="paa-card paa-tblwrap">
      <table class="paa-tbl">
        <thead>
          <tr>
            <th :class="thClass('userId')" @click="sortBy('userId')">{{ sortMark('userId') }}User ID</th>
            <th :class="thClass('userEmail')" @click="sortBy('userEmail')">{{ sortMark('userEmail') }}Email</th>
            <th :class="thClass('id', 'paa-r')" @click="sortBy('id')">{{ sortMark('id') }}Order ID</th>
            <th :class="thClass('orderDate')" @click="sortBy('orderDate')">{{ sortMark('orderDate') }}Order date</th>
            <th>Booster</th>
            <th>Pack</th>
            <th>SEO Manager</th>
            <th :class="thClass('amount', 'paa-r')" @click="sortBy('amount')">{{ sortMark('amount') }}Amount ($)</th>
            <th>Delivery</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in records" :key="row.key">
            <td>{{ row.userId }}</td>
            <td>
              <a :href="userAccountsHref(row.userEmail)" target="_blank" rel="noopener">{{ row.userEmail }}</a>
              <span class="paa-sub">{{ row.siteName }}</span>
            </td>
            <td class="paa-r">{{ row.id }}</td>
            <td>{{ row.orderDate || '' }}</td>
            <td>{{ row.boosterName }}</td>
            <td>{{ row.packLabel }}</td>
            <td>{{ row.manager ? row.manager.name : '' }}</td>
            <td class="paa-r">{{ money(row.amount) }}</td>
            <td><pa-admin-badge :badge="row.delivery" /></td>
            <td>
              <a
                v-if="row.delivery && row.delivery.key === 'delivered' && row.reportUrl"
                :href="row.reportUrl"
                target="_blank"
                rel="noopener noreferrer"
              >View report</a>
              <button
                v-else-if="row.delivery && row.delivery.key === 'ordered'"
                type="button"
                class="paa-link"
                :disabled="busy"
                @click="confirmDelivery(row)"
              >Confirm delivery</button>
              <button
                v-else-if="row.delivery && row.delivery.key === 'in_delivery'"
                type="button"
                class="paa-link"
                :disabled="busy"
                @click="reportFor = row"
              >Mark delivered</button>
            </td>
          </tr>
          <tr v-if="!records.length">
            <td class="paa-empty" colspan="10">{{ loading ? 'Loading…' : 'No booster order matches this search.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pa-admin-pager :page="page" :page-count="pageCount" @change="setPage" />

    <!-- the report link marks the booster delivered and emails the client the link -->
    <booster-report-modal
      v-if="reportFor"
      :details="`${reportFor.boosterName}, ${reportFor.packLabel}`"
      mode="deliver"
      :busy="busy"
      @close="reportFor = null"
      @confirm="markDelivered"
    />
  </div>
</template>

<script>
import BoosterReportModal from '@/components/SitesSingleView/PopAuthority/BoosterReportModal.vue'
import { showPopAuthorityToast } from '@/utils/popAuthority'
import PaAdminBadge from './PaAdminBadge.vue'
import PaAdminFilter from './PaAdminFilter.vue'
import PaAdminPager from './PaAdminPager.vue'
import PaAdminToolbar from './PaAdminToolbar.vue'
import paAdminListMixin from './paAdminListMixin'

/**
 * Boosters Timeline: the paid booster orders and their delivery. "Confirm delivery" starts the
 * work (Ordered to In delivery); "Mark delivered" asks for the report link, which marks the
 * booster delivered and emails the client the link; "View report" opens it.
 */
export default {
  name: 'AdminPopAuthorityBoostersTimeline',
  components: { BoosterReportModal, PaAdminBadge, PaAdminFilter, PaAdminPager, PaAdminToolbar },
  mixins: [paAdminListMixin],
  paAdmin: { action: 'getPopAuthorityBoosterOrdersForAdmin', sort: 'orderDate', tab: 'boosters' },
  data () {
    return { busy: false, reportFor: null }
  },
  methods: {
    replaceRow (record) {
      const index = this.records.findIndex((row) => row.key === record.key)
      if (index !== -1) this.$set(this.records, index, record)
    },
    runAction (action, payload, successText) {
      if (this.busy) return Promise.resolve(false)
      this.busy = true
      return this.$store.dispatch(action, payload)
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') {
            this.notifyError((data && data.msg) || 'The booster could not be updated.')
            return false
          }
          if (data.record) this.replaceRow(data.record)
          showPopAuthorityToast(this, successText)
          return true
        })
        .catch((error) => {
          this.notifyError(this.errorText(error, 'The booster could not be updated.'))
          // somebody else moved the order: show where it stands now
          if (error && error.response && error.response.status === 409) this.loadData()
          return false
        })
        .finally(() => {
          this.busy = false
        })
    },
    confirmDelivery (row) {
      return this.runAction(
        'startPopAuthorityBoosterDeliveryForAdmin', { orderId: row.id },
        `${row.boosterName}, ${row.packLabel}: delivery started`)
    },
    markDelivered ({ reportUrl }) {
      const row = this.reportFor
      if (!row) return null
      return this.runAction(
        'deliverPopAuthorityBoosterForAdmin', { orderId: row.id, body: { reportUrl } },
        `${row.boosterName}, ${row.packLabel}: delivered, the client was emailed the link`,
      ).then((done) => {
        if (done) this.reportFor = null
      })
    },
    exportRows () {
      const header = [
        'User ID', 'Email', 'Project', 'Order ID', 'Order date', 'Booster', 'Pack', 'SEO Manager', 'Amount ($)',
        'Delivery', 'Delivered on', 'Report',
      ]
      return this.exportCsv(header, (row) => [
        row.userId, row.userEmail, row.siteName, row.id, row.orderDate || '', row.boosterName, row.packLabel,
        row.manager ? row.manager.name : '', row.amount, row.delivery ? row.delivery.label : '',
        row.deliveredAt || '', row.reportUrl || '',
      ])
    },
  },
}
</script>
